import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from '@mui/material';
import { Report, models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { usePostApiPowerbiEmbedTokenMutation } from '../../app/athenaApiGen';
import './PowerBi.css';

type TPowerBiEmbeddedProps = {
  reportId: string;
  datasetId: string;
  label?: string;
  onRefreshReady?: (fn: () => Promise<void>) => void;
  // When true, scale the whole report to fit the container (no zoom/scroll)
  // instead of the default fit-to-width behavior.
  fitToPage?: boolean;
};

type TEmbedResult = {
  embedUrl: string;
  accessToken: string;
};

interface PowerBIErrorDetails {
  message: string;
  detailedMessage?: string;
}

const PowerBiEmbedded = (props: TPowerBiEmbeddedProps) => {
  const [embed, setEmbed] = useState<TEmbedResult | null>(null);
  const [notification, setNotification] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshFnRef = useRef<(() => Promise<void>) | null>(null);
  const [createEmbeddedUrl, { isLoading, error }] =
    usePostApiPowerbiEmbedTokenMutation();

  const clearStates = () => {
    setEmbed(null);
    refreshFnRef.current = null;
  };

  useEffect(() => {
    let mounted = true;
    if (!props.reportId) {
      clearStates();
      return;
    }

    const load = async () => {
      try {
        const resp = await createEmbeddedUrl({
          handlersGetEmbedTokenRequest: {
            reportIds: [props.reportId],
            datasetIds: [props.datasetId],
          },
        }).unwrap();

        const result: TEmbedResult = {
          embedUrl: resp.reports?.[0]?.embedUrl || '',
          accessToken: resp.token || '',
        };

        if (!mounted) return;
        setEmbed(result);
      } catch (e) {
        if (!mounted) return;
        clearStates();
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [props.reportId, createEmbeddedUrl]);

  const handleRefresh = async () => {
    if (!refreshFnRef.current || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshFnRef.current();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return <Box sx={{ height: '100%' }}>Loading report…</Box>;
  }

  if (error) {
    return (
      <Box sx={{ height: '100%' }}>
        <Typography>Report failed to load.</Typography>
        {/* show fallback link if embedUrl exists in error/previous response */}
      </Box>
    );
  }

  if (!embed?.embedUrl || !embed?.accessToken) {
    return (
      <Box sx={{ height: '100%' }}>
        <Typography>Report not available.</Typography>
      </Box>
    );
  }

  const handleNotificationClose = (
    event: Event | SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setNotification('');
  };

  return (
    <React.Fragment>
      <Snackbar
        open={notification.length > 0}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {notification}
        </Alert>
      </Snackbar>
      {/* <IconButton
        onClick={handleRefresh}
        disabled={isRefreshing}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 10,
          opacity: 0.2,
          transition: 'opacity 0.2s ease',
          bgcolor: 'background.paper',
          color: 'primary.main',
          '&:hover': {
            opacity: 1,
            bgcolor: 'background.paper',
          },
          '&.Mui-disabled': {
            opacity: 0.05,
          },
        }}
      >
        <RefreshIcon fontSize="small" />
      </IconButton> */}
      <PowerBIEmbed
        embedConfig={{
          type: 'report', // Supported types: report, dashboard, tile, visual, qna, paginated report and create
          id: props.reportId,
          embedUrl: embed.embedUrl,
          accessToken: embed.accessToken,
          datasetIds: [props.datasetId],
          tokenType: models.TokenType.Embed,

          settings: {
            panes: {
              filters: {
                expanded: false,
                visible: false,
              },
              pageNavigation: {
                visible: false,
              },
            },
            bars: {
              actionBar: {
                visible: false,
              },
            },
            background: models.BackgroundType.Transparent,
            ...(props.fitToPage && {
              layoutType: models.LayoutType.Custom,
              customLayout: {
                displayOption: models.DisplayOption.FitToPage,
              },
            }),
          },
        }}
        getEmbeddedComponent={(embeddedReport) => {
          const r = embeddedReport as Report;
          const refreshFn = async () => {
            try {
              await r.reload();
            } catch (err) {
              const { detailedMessage, message } = (err as PowerBIErrorDetails) || {};
              setNotification(
                `Failed to refresh: ${detailedMessage || message || 'Unknown error'}`,
              );
            }
          };
          refreshFnRef.current = refreshFn;
          props.onRefreshReady?.(refreshFn);
        }}
        cssClassName={'Report-class'}
      />
    </React.Fragment>
  );
};

export default PowerBiEmbedded;
